import React from 'react';
import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import { linearGradientDef } from '@nivo/core';
import { adjustColor, generateGradientsAndColors } from './utils'; // Assume similar utils as in Bar chart example

// Assuming the adjustColor and generateGradientsAndColors functions are similar to those used in your Bar chart example,
// but now located in a separate utils file for shared usage.

const nivoScatterPlotTheme = {
  axis: {
    ticks: {
      text: {
        fontSize: 12,
        fontWeight: '600',
        fontFamily: 'Inter, sans-serif',
      },
    },
  },
  legends: {
    text: {
      fontSize: 12,
    },
  },
  tooltip: {
    container: {
      background: 'white',
      color: 'inherit',
      fontSize: 'inherit',
      borderRadius: '2px',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
      padding: '5px 9px',
    },
    basic: {
      whiteSpace: 'pre',
      display: 'flex',
      alignItems: 'center',
    },
    chip: {
      marginRight: '7px',
    },
    table: {},
    tableCell: {
      padding: '3px 5px',
    },
  },
};



const getXYRange = (xValues, yValues)=>{
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);
    const xRange = xMax - xMin;
    const yRange = yMax - yMin;
    return {xMin, xMax, yMin, yMax, xRange, yRange};
}


/**
 *
 * @param data {Array[object]}
 *
 *  Example:
 *  [
 *    {
 *       id: <label> "mySeries",
 *       data: [
 *         {x: <float>, y: <float>},
 *         {...} // optional multiple points per scatter group
 *       ]
 *     },
 *     {...} // optional multiple scatter groups
 *
 * ]
 * @param keys
 * @param customColors
 * @param axisBottomLegend
 * @param axisLeftLegend
 * @param symbolSize
 * @param xPadding
 * @param yPadding
 * @param showLabels
 * @param labelPosition
 * @param dotSize
 * @returns {JSX.Element}
 * @constructor
 */
const NivoScatterChart = ({
  data,
  keys,
  customColors,
  axisBottomLegend = '',
  axisLeftLegend = '',
  symbolSize = 10,
  xPadding = 1, // Added padding for the x axis
  yPadding = 1, // Added padding for the y axis
  showLabels=false,
  labelPosition='top',
  dotSize=10
}) => {
  const { gradients, colorMapping } = generateGradientsAndColors(keys, customColors);
  const colorsToUse = customColors ? customColors : colorMapping;

  const xValues = data.reduce((acc, series) => acc.concat(series.data.map(d => d.x)), []);
  const yValues = data.reduce((acc, series) => acc.concat(series.data.map(d => d.y)), []);

  const { xMin, xMax, yMin, yMax, xRange, yRange } = getXYRange(xValues, yValues);


  return (
    <ResponsiveScatterPlot
      data={data}
      renderNode={showLabels ? ({ node, x, y, size }) => (
        <>
          <circle cx={x} cy={y} r={size / 2} fill={node.style.color} />
          <text
            x={x} y={y + (labelPosition === 'top' ? -size : size + 4)} // Adjust y based on labelPosition
            textAnchor="middle"
            alignmentBaseline={labelPosition === 'top' ? 'baseline' : 'hanging'}
            style={{ fontSize: '10px', fill: '#666', fontWeight: 'bold' }}
          >
            {node.data.serieId}: {node.data.xFormatted}, {node.data.yFormatted}
          </text>
        </>
      ) : undefined}
      nodeSize={typeof dotSize === 'function' ? dotSize : () => dotSize}
      xScale={{
        type: 'linear',
        min: xMin - xRange * xPadding, // Add padding based on data range
        max: xMax + xRange * xPadding,
      }}
      yScale={{
        type: 'linear',
        min: yMin - yRange * yPadding, // Add padding based on data range
        max: yMax + yRange * yPadding,
      }}
      theme={nivoScatterPlotTheme}
      margin={{ top: 20, right: 50, bottom: 50, left: 50 }}
      blendMode="multiply"
      colors={node => colorsToUse[node.serieId]}
      axisTop={null}
      axisRight={null}
      axisBottom={{
        orient: 'bottom',
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: axisBottomLegend,
        legendPosition: 'middle',
        legendOffset: 46,
      }}
      axisLeft={{
        orient: 'left',
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: axisLeftLegend,
        legendPosition: 'middle',
        legendOffset: -40,

      }}
      legends={[
        {
          anchor: 'top-right',
          direction: 'column',
          justify: false,
          translateX: 130,
          translateY: 0,
          itemsSpacing: 0,
          itemWidth: 250,
          itemHeight: 20,
          itemTextColor: '#999',
          symbolSize: symbolSize,
          symbolShape: 'circle',
          effects: [
            {
              on: 'hover',
              style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1,
              },
            },
          ],
        },
      ]}
      defs={gradients}
      fill={keys.map((key, index) => ({
        match: {
          id: key,
        },
        id: `gradient${index}`,
      }))}
      tooltip={({ node }) => (
        <div>
          <strong style={{ color: colorMapping[node.serieId] }}>
            {node.serieId}:
          </strong>
          <br />
          x: {node.data.xFormatted}, y: {node.data.yFormatted}
        </div>
      )}
      animate={true}
      motionStiffness={90}
      motionDamping={15}
    />
  );
};

export default NivoScatterChart;
